import { CancelProgress } from '@components/progress/cancelProgress';
import { DoneProgress } from '@components/progress/doneProgress';
import { TodoProgress } from '@components/progress/todoProgress';
import type { AnalyticsModel } from '@models/analytics.model';
import { JobExecutionStatus, JobStatus } from '@stratumn/jobs';
import { GitCommitHorizontal, Loader2 } from 'lucide-react';
import { observer } from 'mobx-react';

export const analyticsStatusToProgress = (status: JobStatus) => {
  switch (status) {
    case JobStatus.PAUSED:
      return <TodoProgress size={14} />;
    case JobStatus.ACTIVE:
      return <DoneProgress color="#28a745" size={14} />;
  }
};

export const podStatusToProgress = (status: JobExecutionStatus) => {
  switch (status) {
    case JobExecutionStatus.PENDING:
      return <TodoProgress size={14} />;
    case JobExecutionStatus.RUNNING:
      return <Loader2 className="h-4 w-4 animate-spin" color="#4b35d2" />;
    case JobExecutionStatus.FAILED:
      return <CancelProgress color="#eb5757" size={14} />;
    case JobExecutionStatus.COMPLETED:
      return <DoneProgress color="#28a745" size={14} />;
  }
};

type AnalyticsProps = {
  analytics: AnalyticsModel;
};

const AnalyticsCard = ({ analytics }: AnalyticsProps) => {
  return (
    <div
      className="mt-4 mb-11 rounded-md border bg-white p-5 font-normal text-sm shadow-sm"
      style={{
        borderColor: '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.04)'
      }}
    >
      <div className="mb-5 flex">
        <div className="flex flex-1 flex-col leading-6">
          <div className="text-zinc-500">ETL name</div>
          <div className="font-medium text-zinc-950">{analytics.name}</div>
        </div>
        <div className="flex flex-1 flex-col leading-6">
          <div className="text-zinc-500">ETL Status</div>
          <div className="flex flex-row items-center">
            {analytics.status !== undefined &&
              analyticsStatusToProgress(analytics.status)}
            <div className="ml-2 font-medium text-zinc-950">
              {analytics.status}
            </div>
          </div>
        </div>
        {/* TODO: add new fields for new frequency type */}
        {/* <div className="flex flex-1 flex-col leading-6">
          <div className="text-zinc-500">Cron Expression</div>
          <div className="flex flex-row items-center font-medium text-zinc-950">
            {analytics.cron_expression}
          </div>
        </div> */}
      </div>

      <div className="mb-5 flex">
        <div className="flex flex-1 flex-col leading-6">
          <div className="text-zinc-500">Pod ID</div>
          <div className="flex flex-row items-center font-medium text-zinc-950">
            {analytics.jobExecutions?.[0]?.id}
          </div>
        </div>
        <div className="flex flex-1 flex-col leading-6">
          <div className="text-zinc-500">Pod Status</div>
          <div className="flex flex-row items-center">
            {podStatusToProgress(analytics.jobExecutions?.[0]?.status)}
            <div className="ml-2 font-medium text-zinc-950">
              {analytics.jobExecutions?.[0]?.status}
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col leading-6">
          <div className="text-zinc-500">Triggered at</div>
          <div className="flex flex-row items-center font-medium text-zinc-950">
            {new Intl.DateTimeFormat('en-US', {
              dateStyle: 'medium',
              timeStyle: 'short'
            }).format(
              analytics.jobExecutions?.[0]?.executionTime
                ? new Date(analytics.jobExecutions?.[0]?.executionTime)
                : undefined
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col leading-6">
        <div className="text-zinc-500">Image</div>
        <div className="flex flex-row items-center">
          <GitCommitHorizontal size={18} />
          <div className="ml-2 font-medium text-zinc-950">
            {analytics.data.image}
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(AnalyticsCard);
